import { CheckIcon, X } from 'lucide-react-native';
import { type ComponentRef, useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { Keyboard } from 'react-native';
import { twJoin } from 'tailwind-merge';
import { useStore } from 'zustand';

import type { StatementReferenceHelpRequest } from '../../model/extractedStatements';
import { getStatementTriageStore, selectDraftText } from '../../state/statementTriageStore';

import { Checkbox, CheckboxIcon, CheckboxIndicator } from '@/components/ui/checkbox';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

const DESCRIPTION_MAX_LENGTH = 500;

interface StatementReferenceHelpRequestRowProps {
  /** Parent message Twilio SID. */
  messageSid: string;
  /** 0-based statement index this row corresponds to. */
  statementIndex: number;
  /** The persisted help-request entry for this statement, or
   * `undefined` if none has been published yet. Drives initial
   * hydration of the row's draft; the row's local Zustand store
   * becomes the source of truth once the user types. */
  helpRequest: StatementReferenceHelpRequest | undefined;
  /** True while the citation request is publishing. Disables the
   * input + clear control so the user can't edit mid-flight. */
  isPublishing: boolean;
  /** True when this is the first row in its list container, so the
   * separating top border is omitted (the container's own border
   * already encloses the first row). Defaults to `false` — a row
   * rendered on its own keeps the separator. */
  isFirst?: boolean;
}

/**
 * Per-statement free-text help-request row inside the citation wizard.
 * The expert types an article name or description when no auto-suggested
 * candidate matches. The trimmed description is persisted at publish time.
 *
 * # Visual contract
 *
 * - Renders as the trailing row of the candidate list container,
 *   separated from the rows above by a single top hairline (omitted
 *   when `isFirst`).
 * - Empty draft: neutral row, unchecked, trailing clear (X) button
 *   rendered in a muted disabled state so the row's shape doesn't
 *   shift between empty and filled.
 * - Non-empty draft: primary-tinted row, checkbox auto-checked, the
 *   same trailing X enabled for clearing.
 * - The label carries the prompt ("Not finding what you're looking
 *   for?"); the input placeholder carries the instruction ("Add details
 *   and Primum will find it").
 * - Tapping the unchecked checkbox focuses the description input so the
 *   user can start typing without a second tap.
 *
 * # Lifecycle
 *
 * - Empty on publish: no-op.
 * - Non-empty on publish: description forwarded to the backend with the
 *   approval payload; backend reconciles the stored help requests.
 * - Clear button pressed: empties the draft; checkbox auto-unchecks. Any
 *   previously persisted entry is removed on next publish.
 * - Persistence is publish-time only.
 *
 * # LMP visibility
 *
 * Pending entries are author-only. The LMP sees a reference only after
 * Primum staff attaches a real URL.
 *
 * # Cross-platform notes
 *
 * The clear button reaches the 44+px tap target threshold on both
 * touch (via expanded hit area) and mouse (via visible padding).
 */
export function StatementReferenceHelpRequestRow({
  messageSid,
  statementIndex,
  helpRequest,
  isPublishing,
  isFirst,
}: StatementReferenceHelpRequestRowProps) {
  const store = useMemo(() => getStatementTriageStore(messageSid), [messageSid]);

  const descriptionDraft = useStore(store, (state) => selectDraftText(state, statementIndex));

  const setDescriptionDraftAction = useStore(store, (state) => state.setDescriptionDraft);

  const hydrateFromPersistedAction = useStore(store, (state) => state.hydrateFromPersisted);

  const setDescriptionDraft = useCallback(
    (next: string) => setDescriptionDraftAction(statementIndex, next),
    [setDescriptionDraftAction, statementIndex]
  );

  const labelId = useId();

  const helperId = useId();

  useEffect(() => {
    hydrateFromPersistedAction(statementIndex, helpRequest?.description ?? '');
  }, [helpRequest?.description, hydrateFromPersistedAction, statementIndex]);

  const trimmedLength = descriptionDraft.trim().length;

  const isChecked = trimmedLength > 0;

  const isOverLimit = descriptionDraft.length > DESCRIPTION_MAX_LENGTH;

  const inputRef = useRef<ComponentRef<typeof InputField>>(null);

  const handleClear = useCallback(() => {
    setDescriptionDraft('');
  }, [setDescriptionDraft]);

  /**
   * Tap the checkbox while checked to clear, matching universal
   * checkbox affordance (clicking a checked checkbox unchecks).
   * Tap while unchecked focuses the description input so the user
   * can start typing immediately — the checkbox reads as an intent
   * signal ("I need help with this one"), and focus removes the
   * extra tap users would otherwise need on the field below.
   * The trailing X is a second clear path for users reaching for the
   * row's trailing edge; both paths feel redundant but each matches a
   * different user expectation (control toggle vs. close affordance).
   */
  const handleToggleCheckbox = useCallback(() => {
    if (isChecked) {
      setDescriptionDraft('');

      return;
    }

    // @ts-expect-error Gluestack InputField ref drops the underlying TextInput.focus()
    inputRef.current?.focus();
  }, [isChecked, setDescriptionDraft]);

  const checkboxLabel = isChecked
    ? `Reference description: ${descriptionDraft}. Tap to clear.`
    : 'Reference description, not yet filled in';

  return (
    <VStack
      className={twJoin(!isFirst && 'border-t border-outline-100', isChecked && 'bg-primary-50')}>
      <HStack className="items-start gap-2.5 px-3 py-2">
        <Checkbox
          value={`help-request-${statementIndex}`}
          isChecked={isChecked}
          onChange={handleToggleCheckbox}
          isDisabled={isPublishing}
          aria-label={checkboxLabel}>
          <CheckboxIndicator>
            <CheckboxIcon as={CheckIcon} />
          </CheckboxIndicator>
        </Checkbox>
        <VStack className="min-w-0 flex-1" space="xs">
          <Text size="sm" className="font-medium text-typography-900" nativeID={labelId}>
            Not finding what you're looking for?
          </Text>
          <Input
            size="md"
            isDisabled={isPublishing}
            isInvalid={isOverLimit}
            className="bg-background-0">
            <InputField
              ref={inputRef}
              placeholder="Add details and Primum will find it"
              value={descriptionDraft}
              onChangeText={setDescriptionDraft}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="done"
              onSubmitEditing={Keyboard.dismiss}
              accessibilityLabelledBy={labelId}
              aria-describedby={isOverLimit ? helperId : undefined}
            />
          </Input>
        </VStack>
        <Pressable
          onPress={handleClear}
          disabled={isPublishing || !isChecked}
          hitSlop={ICON_BUTTON_HIT_SLOP}
          className={twJoin(
            '-mr-1 -mt-1 items-center p-1 web:cursor-pointer',
            !isChecked && 'opacity-40 web:cursor-default'
          )}
          {...getAccessibilityProps({
            role: 'button',
            label: isChecked
              ? 'Clear reference description'
              : 'Clear reference description, nothing to clear',
          })}>
          <X className="text-typography-500" size={20} />
        </Pressable>
      </HStack>
      {isOverLimit ? (
        <Text
          size="xs"
          nativeID={helperId}
          className="px-3 pb-2 text-warning-700"
          accessibilityLiveRegion="polite">
          Please keep it under {DESCRIPTION_MAX_LENGTH} characters ({descriptionDraft.length} now).
        </Text>
      ) : null}
    </VStack>
  );
}
